// Retain legacy classes, with restrained upward reveals for ordinary content.
const directions = [
    { selector: '.has-fade-in-up', x: 0, y: 16 },
    { selector: '.has-fade-in-left', x: -24, y: 0 },
    { selector: '.has-fade-in-right', x: 24, y: 0 },
];

function revealDirection(element) {
    const direction = directions.find(({ selector }) => element.matches(selector));
    const content = element.closest('.wp-block-post-content');
    if (!content) return direction;

    // The opening content block containing the page title is the hero boundary.
    // Other headings, columns, tabs, and nested blocks always fade upward.
    const hero = Array.from(content.children).find((block) => block.matches('h1') || block.querySelector('h1'));
    return hero?.contains(element) ? direction : directions[0];
}

function initFadeIns() {
    const elements = document.querySelectorAll(directions.map(({ selector }) => selector).join(','));
    if (!elements.length) return;

    // Ignore filters and anchors: refreshing or returning to this page must not replay it.
    const page = window.location.pathname.replace(/\/+$/, '') || '/';
    try {
        const key = `coto:page-reveal:v1:${page}`;
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, '1');
    } catch {
        // If the browser cannot remember visits, leave everything visible.
        return;
    }

    if (!window.IntersectionObserver || !Element.prototype.animate || !window.matchMedia) return;
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionPreference.matches) return;

    const animations = new Map();
    let observer;
    let resizeFrame;

    function showEverything() {
        observer?.disconnect();
        cancelAnimationFrame(resizeFrame);
        animations.forEach((animation) => animation.cancel());
        animations.clear();
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('pagehide', showEverything);
        window.removeEventListener('beforeprint', showEverything);
        document.removeEventListener('focusin', handleFocus);
        motionPreference.removeEventListener('change', handleMotionChange);
    }

    function revealImmediately(element) {
        observer?.unobserve(element);
        animations.get(element)?.cancel();
        animations.delete(element);
        if (!animations.size) showEverything();
    }

    function observePending() {
        observer?.disconnect();
        observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (!isIntersecting) return;
                observer.unobserve(target);
                animations.get(target)?.play();
            });
        }, {
            // Use pixels because percentage root margins are based on viewport width.
            rootMargin: `0px 0px -${Math.round(window.innerHeight * 0.1)}px 0px`,
            threshold: 0,
        });
        animations.forEach((animation, element) => {
            if (animation.playState === 'paused') observer.observe(element);
        });
    }

    function handleResize() {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(observePending);
    }

    function handleMotionChange(event) {
        if (event.matches) showEverything();
    }

    function handleFocus(event) {
        animations.forEach((animation, element) => {
            if (element.contains(event.target)) revealImmediately(element);
        });
    }

    try {
        elements.forEach((element) => {
            const { x, y } = revealDirection(element);
            const style = getComputedStyle(element);
            const transform = style.transform === 'none' ? '' : style.transform;
            const animation = element.animate([
                { opacity: 0, transform: `translate3d(${x}px, ${y}px, 0) ${transform}`.trim() },
                { opacity: style.opacity, transform: style.transform },
            ], {
                duration: 650,
                // Quadratic ease-out, matching the accepted motion curve.
                easing: 'cubic-bezier(0.333333, 0.666667, 0.666667, 1)',
                fill: 'both',
            });
            animations.set(element, animation);
            animation.pause();
            animation.currentTime = 0;
            animation.onfinish = () => revealImmediately(element);
        });

        observePending();
        window.addEventListener('resize', handleResize, { passive: true });
        // Finish pending reveals before the browser saves a page for Back/Forward navigation.
        window.addEventListener('pagehide', showEverything);
        window.addEventListener('beforeprint', showEverything);
        document.addEventListener('focusin', handleFocus);
        motionPreference.addEventListener('change', handleMotionChange);
    } catch {
        showEverything();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFadeIns, { once: true });
} else {
    initFadeIns();
}
